import { AppliedTaxes } from "../types/budget/service-item";
import {
  CBS_IBS_PILLS_ENABLED,
  isAppliedTaxPillVisible,
  isDefaultTaxPillVisible,
} from "./appliedTaxesByCurrency";

export const regexNumber = /^[0-9\b]+$/;

export const regexEmail =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function isDateValid(date: string) {
  const dateObj = new Date(date);
  return dateObj.getTime() === dateObj.getTime();
}

export function uuid() {
  var temp_url = URL.createObjectURL(new Blob());
  var uuid = temp_url.toString();
  URL.revokeObjectURL(temp_url);
  return uuid.substr(uuid.lastIndexOf("/") + 1); // remove prefix (e.g. blob:null/, blob:www.test.com/, ...)
}

/** Contexto das pills: define quais taxas o usuário realmente enxerga/edita. */
export type TaxPillContext = {
  /** Moeda efetiva da categoria — a mesma passada às pills de Taxas padrão. */
  currency?: string;
  /** Escopo do evento (nacional força BRL nas pills). */
  eventScope?: string;
};

/**
 * Compara as taxas aplicadas de um serviço com as taxas padrão (mainAppliedTaxes)
 * @param serviceAppliedTaxes - Taxas aplicadas ao serviço específico
 * @param mainAppliedTaxes - Taxas padrão da categoria/fornecedor
 * @param pillContext - Moeda/escopo da categoria; sem ele só as taxas sem pill
 *                      (overhead, imposto fornecedor, CBS/IBS desligados) são ignoradas
 * @returns true se as taxas do serviço são diferentes das taxas padrão
 */
export function hasCustomTaxes(
  serviceAppliedTaxes: AppliedTaxes | undefined,
  mainAppliedTaxes: AppliedTaxes | undefined,
  markupEnabled: boolean = false,
  commissionEnabled: boolean = false,
  supplierTributeEnabled: boolean = false,
  pillContext?: TaxPillContext
): boolean {
  if (!serviceAppliedTaxes || !mainAppliedTaxes) {
    return false;
  }

  // Só entram na comparação as taxas que viram pill nesta categoria. Uma taxa
  // sem pill não pode ser conferida nem corrigida pelo usuário, então uma
  // divergência nela vira um alerta permanente e invisível — foi o que
  // aconteceu com o IOF em orçamentos BRL (serviço `iof: true` × categoria
  // `iof: false` vindo de dados antigos) e com CBS/IBS enquanto a Reforma
  // Tributária não entra em vigência.
  const isComparableTax = (key: string): boolean =>
    pillContext
      ? isAppliedTaxPillVisible(
          key,
          pillContext.currency ?? "BRL",
          pillContext.eventScope
        )
      : // Sem contexto de moeda não dá pra saber se ISS/IOF têm pill aqui, então
        // só descartamos o que fica oculto em qualquer moeda.
        isDefaultTaxPillVisible(key) &&
        (CBS_IBS_PILLS_ENABLED || (key !== "cbs" && key !== "ibs"));

  const taxKeys = Array.from(
    new Set([
      ...Object.keys(serviceAppliedTaxes ?? {}),
      ...Object.keys(mainAppliedTaxes ?? {}),
    ])
  ).filter(isComparableTax) as (keyof AppliedTaxes)[];

  if (markupEnabled || commissionEnabled) {
    return true;
  }
  // Imposto fornecedor no modal segue o markup; legado pode ter override sem markup.
  if (supplierTributeEnabled && !markupEnabled) {
    return true;
  }

  // Comparação direta, sem normalizar os valores por moeda: forçar os dois
  // lados para a mesma moeda antes de comparar apagava diferenças manuais
  // reais (ex.: usuário desmarca o IOF de um serviço específico em moeda
  // estrangeira e o alerta nunca aparecia). O recorte por moeda acontece só
  // em `isComparableTax`, que decide *quais* taxas têm pill — o ISS continua
  // comparado em BRL e o IOF em moeda estrangeira, cada um onde é editável.
  for (const key of taxKeys) {
    if (Boolean(serviceAppliedTaxes[key]) !== Boolean(mainAppliedTaxes[key])) {
      return true;
    }
  }

  return false;
}
